import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, AlertTriangle, PackageX, TruckIcon, XCircle, CheckCircle2, RefreshCw, Sparkles, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";

/*
  Pulls real alerts from /api/command-center/alerts and adds synthetic
  "you have unseen messages" style items. Auto-refreshes every 60s.
*/

const KIND_ICON = {
  sla_breach: AlertTriangle,
  dlq_backlog: RefreshCw,
  sync_failure: XCircle,
  low_stock: PackageX,
  courier_delay: TruckIcon,
  qc_pending: MessageSquare,
  ai_recommendation: Sparkles,
  info: CheckCircle2,
};
const KIND_TONE = {
  critical: "chip-danger",
  high: "chip-danger",
  medium: "chip-warning",
  low: "chip-info",
};

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const nav = useNavigate();

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/command-center/alerts");
      setAlerts(data?.alerts || []);
    } catch { /* keep prev */ } finally { setLoading(false); }
  };

  useEffect(() => {
    fetch();
    const t = setInterval(fetch, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (!boxRef.current?.contains(e.target) && !e.target.closest?.("[data-testid='v2-notif-btn']")) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const count = alerts.length;

  return (
    <div style={{ position: "relative" }}>
      <button
        className="v2-btn v2-btn-ghost v2-btn-icon"
        onClick={() => setOpen((v) => !v)}
        data-testid="v2-notif-btn"
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell size={16} strokeWidth={1.9} />
        {count > 0 && (
          <span style={{
            position: "absolute", top: 3, right: 3,
            minWidth: 16, height: 16, padding: "0 4px",
            borderRadius: 999,
            background: "var(--v2-danger)",
            color: "#fff",
            fontSize: 10, fontWeight: 700,
            display: "grid", placeItems: "center",
            border: "2px solid var(--v2-bg-2)",
          }}>{count > 99 ? "99+" : count}</span>
        )}
      </button>

      {open && (
        <div
          ref={boxRef}
          className="v2-pop"
          style={{
            position: "absolute", right: 0, top: "calc(100% + 8px)",
            width: 380, maxWidth: "calc(100vw - 24px)",
            maxHeight: 520, display: "flex", flexDirection: "column",
          }}
          data-testid="v2-notif-panel"
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--v2-border)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontFamily: "var(--v2-font-display)", fontWeight: 600, fontSize: 14 }}>Notifications</div>
              {count > 0 && <span className="v2-chip v2-chip-primary">{count} active</span>}
            </div>
            <button className="v2-btn v2-btn-ghost v2-btn-icon" onClick={fetch} title="Refresh" style={{ width: 24, height: 24 }}>
              <RefreshCw size={12} className={loading ? "v2-spin" : ""} style={loading ? { animation: "spin 900ms linear infinite" } : undefined} />
            </button>
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {alerts.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: "var(--v2-text-muted)", fontSize: 13 }}>
                <CheckCircle2 size={30} color="var(--v2-success)" style={{ margin: "0 auto 10px", display: "block" }} />
                You're all caught up.
              </div>
            )}
            {alerts.map((a) => {
              const Icon = KIND_ICON[a.kind] || Bell;
              const tone = KIND_TONE[a.severity] || "chip-info";
              return (
                <button
                  key={a.id || `${a.kind}-${a.title}`}
                  onClick={() => { if (a.link) nav(a.link); setOpen(false); }}
                  data-testid={`v2-notif-${a.id || a.kind}`}
                  style={{
                    display: "flex", gap: 12, padding: "12px 16px",
                    width: "100%", background: "transparent",
                    border: 0, borderBottom: "1px solid var(--v2-divider)",
                    textAlign: "left", cursor: "pointer",
                    color: "var(--v2-text)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--v2-surface-hover)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--v2-surface-hover)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                    <Icon size={14} strokeWidth={1.9} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{a.title || a.kind}</div>
                      <span className={`v2-chip v2-${tone}`}>{a.severity || "info"}</span>
                    </div>
                    {a.detail && <div style={{ fontSize: 12, color: "var(--v2-text-muted)", marginTop: 2, lineHeight: 1.4 }}>{a.detail}</div>}
                    {a.action && <div style={{ fontSize: 11, color: "var(--v2-primary-2)", marginTop: 4, fontWeight: 600 }}>{a.action} →</div>}
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{ padding: 10, borderTop: "1px solid var(--v2-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button
              className="v2-btn v2-btn-ghost v2-btn-sm"
              onClick={() => { setOpen(false); nav("/command"); }}
              data-testid="v2-notif-view-all"
            >
              Open Command Center
            </button>
            <button className="v2-btn v2-btn-ghost v2-btn-icon" onClick={() => setOpen(false)} style={{ width: 24, height: 24 }} title="Close">
              <X size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
